/**
 * @fileoverview Tiny in-memory cache shared between the list hydration and the
 * drawers, keyed by `identifierId`. Lets the list prefetch a task's detail and
 * the drawer reuse it (no double fetch). Dedupes in-flight requests too.
 */

const values = new Map<string, unknown>()
const inFlight = new Map<string, Promise<unknown>>()

/** Returns a cached value (if any) for the given key. */
export function getCached<T>(key: string): T | undefined {
  return values.get(key) as T | undefined
}

/** Stores a value for the given key. */
export function setCached<T>(key: string, value: T): void {
  values.set(key, value)
}

/** Drops a cached value (e.g. after a submit, to force a fresh fetch). */
export function invalidate(key: string): void {
  values.delete(key)
  inFlight.delete(key)
}

/**
 * Returns the cached value or runs `loader` once, sharing the in-flight promise
 * across concurrent callers and caching the result.
 */
export async function loadCached<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const cached = values.get(key) as T | undefined
  if (cached !== undefined) return cached

  const pending = inFlight.get(key) as Promise<T> | undefined
  if (pending) return pending

  const promise = loader()
    .then(result => {
      values.set(key, result)
      return result
    })
    .finally(() => inFlight.delete(key))

  inFlight.set(key, promise)
  return promise
}
